import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { usePersistedState } from '../hooks/usePersistedState';
import { storageService } from '../services/storageService';
import { safeParseJSON } from '../utils/storageGuards';

// ==================== TYPES ====================

export interface Session {
    userId: string;
    email: string;
    token: string;
    workspaceRole?: 'owner' | 'editor' | 'viewer';
}

const normalizeWorkspaceRole = (value: unknown): Session['workspaceRole'] => {
    if (value === 'owner' || value === 'editor' || value === 'viewer') return value;
    return undefined;
};

const resolveSessionWorkspaceRole = (workspaceRoleValue: unknown, legacyRoleValue: unknown): Session['workspaceRole'] => {
    const fromWorkspaceRole = normalizeWorkspaceRole(workspaceRoleValue);
    const fromLegacyRole = normalizeWorkspaceRole(legacyRoleValue);
    if (fromWorkspaceRole && fromLegacyRole && fromWorkspaceRole !== fromLegacyRole) {
        const rank: Record<NonNullable<Session['workspaceRole']>, number> = {
            viewer: 1,
            editor: 2,
            owner: 3
        };
        return rank[fromWorkspaceRole] <= rank[fromLegacyRole] ? fromWorkspaceRole : fromLegacyRole;
    }
    return fromWorkspaceRole || fromLegacyRole || 'owner';
};

const normalizeSession = (parsed: any): Session | null => {
    if (!parsed?.userId || !parsed?.email || !parsed?.token) return null;
    const normalizedRole = resolveSessionWorkspaceRole(parsed.workspaceRole, parsed.role);
    return {
        ...parsed,
        workspaceRole: normalizedRole
    } as Session;
};

// ==================== ATOMIC CONTEXT 1: IDENTITY ====================

interface AuthIdentityContextType {
    userId: string | null;
    userEmail: string | null;
    isAuthenticated: boolean;
    isViewer: boolean;
}

const AuthIdentityContext = createContext<AuthIdentityContextType | undefined>(undefined);

export const useAuthIdentity = (): AuthIdentityContextType => {
    const context = useContext(AuthIdentityContext);
    if (context === undefined) {
        throw new Error('useAuthIdentity must be used within AuthProvider');
    }
    return context;
};

// ==================== ATOMIC CONTEXT 2: SESSION ====================

interface AuthSessionContextType {
    session: Session | null;
    setSession: React.Dispatch<React.SetStateAction<Session | null>>;
    sessionLoaded: boolean;
    setSessionLoaded: React.Dispatch<React.SetStateAction<boolean>>;
}

const AuthSessionContext = createContext<AuthSessionContextType | undefined>(undefined);

export const useAuthSession = (): AuthSessionContextType => {
    const context = useContext(AuthSessionContext);
    if (context === undefined) {
        throw new Error('useAuthSession must be used within AuthProvider');
    }
    return context;
};

// ==================== ATOMIC CONTEXT 3: FORM STATE ====================

interface AuthFormContextType {
    sessionEmailInput: string;
    setSessionEmailInput: React.Dispatch<React.SetStateAction<string>>;
    sessionCodeInput: string;
    setSessionCodeInput: React.Dispatch<React.SetStateAction<string>>;
    sessionStage: 'email' | 'verify';
    setSessionStage: React.Dispatch<React.SetStateAction<'email' | 'verify'>>;
    isSessionRequestingCode: boolean;
    setIsSessionRequestingCode: React.Dispatch<React.SetStateAction<boolean>>;
    isSessionSubmitting: boolean;
    setIsSessionSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
    sessionErrorMessage: string | null;
    setSessionErrorMessage: React.Dispatch<React.SetStateAction<string | null>>;
    sessionHintCode: string | null;
    setSessionHintCode: React.Dispatch<React.SetStateAction<string | null>>;
}

const AuthFormContext = createContext<AuthFormContextType | undefined>(undefined);

export const useAuthForm = (): AuthFormContextType => {
    const context = useContext(AuthFormContext);
    if (context === undefined) {
        throw new Error('useAuthForm must be used within AuthProvider');
    }
    return context;
};

// ==================== ATOMIC CONTEXT 4: RATE LIMITING ====================

interface AuthRateLimitContextType {
    sessionResendSeconds: number;
    setSessionResendSeconds: React.Dispatch<React.SetStateAction<number>>;
    sessionAttempts: number;
    setSessionAttempts: React.Dispatch<React.SetStateAction<number>>;
    sessionLockUntil: number | null;
    setSessionLockUntil: React.Dispatch<React.SetStateAction<number | null>>;
}

const AuthRateLimitContext = createContext<AuthRateLimitContextType | undefined>(undefined);

export const useAuthRateLimit = (): AuthRateLimitContextType => {
    const context = useContext(AuthRateLimitContext);
    if (context === undefined) {
        throw new Error('useAuthRateLimit must be used within AuthProvider');
    }
    return context;
};

// ==================== LEGACY COMPOSITE CONTEXT ====================

export interface AuthContextType {
    // Session state
    session: Session | null;
    setSession: React.Dispatch<React.SetStateAction<Session | null>>;
    sessionLoaded: boolean;
    setSessionLoaded: React.Dispatch<React.SetStateAction<boolean>>;

    // Login form state
    sessionEmailInput: string;
    setSessionEmailInput: React.Dispatch<React.SetStateAction<string>>;
    sessionCodeInput: string;
    setSessionCodeInput: React.Dispatch<React.SetStateAction<string>>;
    sessionStage: 'email' | 'verify';
    setSessionStage: React.Dispatch<React.SetStateAction<'email' | 'verify'>>;

    // Loading states
    isSessionRequestingCode: boolean;
    setIsSessionRequestingCode: React.Dispatch<React.SetStateAction<boolean>>;
    isSessionSubmitting: boolean;
    setIsSessionSubmitting: React.Dispatch<React.SetStateAction<boolean>>;

    // Error and hint states
    sessionErrorMessage: string | null;
    setSessionErrorMessage: React.Dispatch<React.SetStateAction<string | null>>;
    sessionHintCode: string | null;
    setSessionHintCode: React.Dispatch<React.SetStateAction<string | null>>;

    // Rate limiting
    sessionResendSeconds: number;
    setSessionResendSeconds: React.Dispatch<React.SetStateAction<number>>;
    sessionAttempts: number;
    setSessionAttempts: React.Dispatch<React.SetStateAction<number>>;
    sessionLockUntil: number | null;
    setSessionLockUntil: React.Dispatch<React.SetStateAction<number | null>>;

    // Derived values
    userId: string | null;
    userEmail: string | null;
    isAuthenticated: boolean;
    isViewer: boolean;

    // Actions
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
    onLogout?: () => void;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, onLogout }) => {
    // Session state
    const legacySession = useMemo(() => {
        if (typeof window === 'undefined') return null;
        try {
            const stored = storageService.getItem('arc_session');
            if (!stored) return null;
            const parsed = safeParseJSON<any>(stored, null);
            return normalizeSession(parsed);
        } catch {
            // ignore invalid storage
        }
        return null;
    }, []);

    const [session, setSession] = usePersistedState<Session | null>(
        'longevityos:session',
        legacySession,
        {
            serialize: (value) => (value ? JSON.stringify(value) : ''),
            deserialize: (raw) => {
                if (!raw) return null;
                try {
                    const parsed = JSON.parse(raw);
                    return normalizeSession(parsed);
                } catch {
                    // ignore invalid storage
                }
                return null;
            }
        }
    );
    const [sessionLoaded, setSessionLoaded] = useState(false);

    // Login form state
    const [sessionEmailInput, setSessionEmailInput] = useState('');
    const [sessionCodeInput, setSessionCodeInput] = useState('');
    const [sessionStage, setSessionStage] = useState<'email' | 'verify'>('email');

    // Loading states
    const [isSessionRequestingCode, setIsSessionRequestingCode] = useState(false);
    const [isSessionSubmitting, setIsSessionSubmitting] = useState(false);

    // Error and hint states
    const [sessionErrorMessage, setSessionErrorMessage] = useState<string | null>(null);
    const [sessionHintCode, setSessionHintCode] = useState<string | null>(null);

    // Rate limiting
    const [sessionResendSeconds, setSessionResendSeconds] = useState(0);
    const [sessionAttempts, setSessionAttempts] = useState(0);
    const [sessionLockUntil, setSessionLockUntil] = useState<number | null>(null);

    // Derived values - memoized to prevent recalculation
    const identityValue = useMemo(() => ({
        userId: session?.userId ?? null,
        userEmail: session?.email ?? null,
        isAuthenticated: session !== null,
        isViewer: session?.workspaceRole === 'viewer' || (session as any)?.role === 'viewer',
    }), [session?.userId, session?.email, session?.workspaceRole, session !== null]);

    const sessionValue = useMemo(() => ({
        session,
        setSession,
        sessionLoaded,
        setSessionLoaded,
    }), [session, sessionLoaded, setSession]);

    const formValue = useMemo(() => ({
        sessionEmailInput,
        setSessionEmailInput,
        sessionCodeInput,
        setSessionCodeInput,
        sessionStage,
        setSessionStage,
        isSessionRequestingCode,
        setIsSessionRequestingCode,
        isSessionSubmitting,
        setIsSessionSubmitting,
        sessionErrorMessage,
        setSessionErrorMessage,
        sessionHintCode,
        setSessionHintCode,
    }), [
        sessionEmailInput, sessionCodeInput, sessionStage,
        isSessionRequestingCode, isSessionSubmitting,
        sessionErrorMessage, sessionHintCode
    ]);

    const rateLimitValue = useMemo(() => ({
        sessionResendSeconds,
        setSessionResendSeconds,
        sessionAttempts,
        setSessionAttempts,
        sessionLockUntil,
        setSessionLockUntil,
    }), [sessionResendSeconds, sessionAttempts, sessionLockUntil]);

    // Logout action
    const logout = useCallback(() => {
        setSession(null);
        setSessionEmailInput('');
        setSessionCodeInput('');
        setSessionStage('email');
        setSessionErrorMessage(null);
        setSessionHintCode(null);
        setSessionAttempts(0);
        setSessionLockUntil(null);

        // Clear legacy persisted session
        if (typeof window !== 'undefined') {
            storageService.removeItem('arc_session');
        }

        // Keep sessionLoaded true so the login form renders instead of the loading gate
        setSessionLoaded(true);

        onLogout?.();
    }, [onLogout, setSession]);

    useEffect(() => {
        setSessionLoaded(true);
    }, []);

    // Resend countdown timer - optimized with useCallback
    useEffect(() => {
        if (sessionResendSeconds <= 0) return;

        const timer = setTimeout(() => {
            setSessionResendSeconds(prev => Math.max(0, prev - 1));
        }, 1000);

        return () => clearTimeout(timer);
    }, [sessionResendSeconds]);

    // Legacy combined context value
    const legacyValue = useMemo(() => ({
        ...identityValue,
        ...sessionValue,
        ...formValue,
        ...rateLimitValue,
        logout,
    }), [identityValue, sessionValue, formValue, rateLimitValue, logout]);

    return (
        <AuthIdentityContext.Provider value={identityValue}>
            <AuthSessionContext.Provider value={sessionValue}>
                <AuthFormContext.Provider value={formValue}>
                    <AuthRateLimitContext.Provider value={rateLimitValue}>
                        <AuthContext.Provider value={legacyValue}>
                            {children}
                        </AuthContext.Provider>
                    </AuthRateLimitContext.Provider>
                </AuthFormContext.Provider>
            </AuthSessionContext.Provider>
        </AuthIdentityContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
